import { computed, reactive, watch, onBeforeUnmount } from 'vue';

// Shared reactive preview state. Exported so both the Vue useCopy and the
// JSX/React-style _useDomainCopy helper can subscribe to the same postMessage
// stream — otherwise live edits would only reach Vue variants.
export const previewState = reactive({
    copyPack: {},
    spotImages: {},
    branding: null,
    designVariant: null,
    service: null,
});

let previewListenerInstalled = false;
export function installPreviewListener() {
    if (previewListenerInstalled) return;
    if (typeof window === 'undefined') return;
    previewListenerInstalled = true;
    window.addEventListener('message', (event) => {
        const data = event?.data;
        if (!data || data.type !== 'pv-preview') return;
        if (data.copyPack) Object.assign(previewState.copyPack, data.copyPack);
        if (data.spotImages) Object.assign(previewState.spotImages, data.spotImages);
        if (data.branding) previewState.branding = data.branding;
        if (data.designVariant) previewState.designVariant = data.designVariant;
        if (data.service) previewState.service = data.service;
    });
}

export function isPreview() {
    return typeof window !== 'undefined' && !!window.__previewMode;
}

/**
 * Returns reactive copy resolvers for a variant page. Drop-in replacement
 * for the inline t/ts/spot helpers each variant defined.
 *
 * Non-preview mode: reads props.copyPack / props.spotImages directly.
 * Preview mode: merges window-level previewState (updated via postMessage)
 * over props, so live-edits in the builder show up without re-rendering
 * the whole Inertia page.
 */
export function useCopy(props) {
    if (isPreview()) {
        installPreviewListener();
        // Seed previewState with the initial props so the first paint matches.
        if (props.copyPack && Object.keys(previewState.copyPack).length === 0) {
            Object.assign(previewState.copyPack, props.copyPack);
        }
        if (props.spotImages && Object.keys(previewState.spotImages).length === 0) {
            Object.assign(previewState.spotImages, props.spotImages);
        }
    }

    const isWP = computed(() => (previewState.service || props.service) === 'wp');
    const suffix = computed(() => (isWP.value ? '_wp' : '_pv'));

    const pack = computed(() => {
        if (isPreview()) {
            return { ...(props.copyPack || {}), ...previewState.copyPack };
        }
        return props.copyPack || {};
    });

    const spotImages = computed(() => {
        if (isPreview()) {
            return { ...(props.spotImages || {}), ...previewState.spotImages };
        }
        return props.spotImages || {};
    });

    // In inline-edit mode the variant registers every resolved (key, rendered)
    // pair on window.__pvFallbacks so the iframe overlay can map clicked text
    // back to a slot key. No-op outside the edit overlay.
    const recordEdit = (resolvedKey, rendered) => {
        if (typeof window === 'undefined') return;
        if (!window.__pvFallbacks) window.__pvFallbacks = {};
        if (typeof rendered === 'string' && rendered.length > 0) {
            window.__pvFallbacks[resolvedKey] = rendered;
        }
    };

    const t = (key, fallback) => {
        const v = pack.value?.[key];
        const out = v !== undefined && v !== null && v !== '' ? v : fallback;
        recordEdit(key, out);
        return out;
    };
    const ts = (baseKey, fallback) => {
        const realKey = baseKey + suffix.value;
        const v = pack.value?.[realKey];
        const out = v !== undefined && v !== null && v !== '' ? v : fallback;
        recordEdit(realKey, out);
        return out;
    };
    const spot = (key) => spotImages.value?.[key] ?? null;

    // Responsive variants via SeoController::media() on-the-fly resize.
    // Mirrors Variants/_useDomainCopy.js so Vue pages can emit srcset.
    const spotSet = (key) => {
        const base = spot(key);
        if (!base || typeof base !== 'string') return null;
        const sep = base.includes('?') ? '&' : '?';
        return {
            src: `${base}${sep}w=800`,
            src400: `${base}${sep}w=400`,
            src800: `${base}${sep}w=800`,
            src1200: `${base}${sep}w=1200`,
            src1600: `${base}${sep}w=1600`,
            fallback: base,
            srcset: `${base}${sep}w=400 400w, ${base}${sep}w=800 800w, ${base}${sep}w=1200 1200w, ${base}${sep}w=1600 1600w`,
        };
    };

    return { isWP, suffix, t, ts, spot, spotSet, pack, spotImages };
}
